import React, { useEffect, useState, useRef } from 'react';
import { IonPage, IonContent, IonRefresher, IonRefresherContent } from '@ionic/react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useListsStore, ShoppingList } from '../../store/listsStore';
import { useTasksStore, Task } from '../../store/tasksStore';
import { usePantryStore, PantryItem } from '../../store/pantryStore';
import { useNotesStore, SharedNote } from '../../store/notesStore';
import { format, parseISO, differenceInDays } from 'date-fns';
import Icon from '../../components/Icon';
import BackHeader from '../../components/BackHeader';
import FeaturePageHeader from '../../components/FeaturePageHeader';
import FeatureMobileShell from '../../components/FeatureMobileShell';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { initials, MEMBER_COLOURS } from '../../utils/constants';
import { showToast } from '../../components/Toast';
import { api } from '../../services/api';

type Tab = 'shopping' | 'tasks' | 'notes';

const TABS: { key: Tab; label: string; icon: string; color: string }[] = [
  { key: 'shopping', label: 'Shopping', icon: 'shopping_cart', color: '#e9c46a' },
  { key: 'tasks', label: 'Tasks', icon: 'check_circle', color: '#81c784' },
  { key: 'notes', label: 'Notes', icon: 'sticky_note_2', color: '#ffb74d' },
];

const ListsHubPage: React.FC = () => {
  const [tab, setTab] = useState<Tab>('shopping');
  const isDesktop = useIsDesktop();
  const history = useHistory();
  const location = useLocation();
  const { user, family } = useAuthStore();
  const members = family?.members || [];
  const newListInputRef = useRef<HTMLInputElement>(null);

  // Stores
  const { lists, fetchLists, createList } = useListsStore();
  const { tasks, fetchTasks, createTask, updateTaskStatus } = useTasksStore();
  const { items: pantryItems, expiringItems, fetchItems: fetchPantry, fetchExpiring } = usePantryStore();
  const { notes, fetchNotes, createNote } = useNotesStore();

  // Local state
  const [newListName, setNewListName] = useState('');
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newNoteTitle, setNewNoteTitle] = useState('');
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => {
    if (family) {
      fetchLists();
      fetchTasks();
      fetchPantry();
      fetchExpiring();
      fetchNotes();
    }
  }, [family]);

  // Focus the "New list name" input when navigated from the dashboard FAB with ?create=1.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('create') === '1') {
      setShowAdd(true);
      // Next tick so the input is mounted when we focus it.
      setTimeout(() => newListInputRef.current?.focus(), 0);
      params.delete('create');
      const nextSearch = params.toString();
      history.replace(`${location.pathname}${nextSearch ? '?' + nextSearch : ''}`);
    }
  }, [location.search]);

  const handleRefresh = async (e: any) => {
    if (family) {
      await Promise.all([fetchLists(), fetchTasks(), fetchPantry(), fetchExpiring(), fetchNotes()]);
    }
    e.detail.complete();
  };

  // Quick add handlers
  const handleAddList = async () => {
    if (!newListName.trim()) return;
    try {
      const list = await createList(newListName.trim());
      setNewListName('');
      setShowAdd(false);
      history.push(`/app/lists/${list.id}`);
    } catch { showToast('Failed to create list', 'error'); }
  };

  const handleAddTask = async () => {
    if (!newTaskTitle.trim()) return;
    try {
      await createTask({ title: newTaskTitle.trim(), priority: 'medium' });
      setNewTaskTitle('');
      setShowAdd(false);
      showToast('Task added', 'success');
    } catch { showToast('Failed to add task', 'error'); }
  };

  const handleAddNote = async () => {
    if (!newNoteTitle.trim()) return;
    try {
      await createNote({ title: newNoteTitle.trim() });
      setNewNoteTitle('');
      setShowAdd(false);
      showToast('Note created', 'success');
    } catch { showToast('Failed to create note', 'error'); }
  };

  const handleToggleTask = async (task: Task) => {
    try {
      const newStatus = task.status === 'done' ? 'pending' : 'done';
      await updateTaskStatus(task.id, newStatus);
    } catch { showToast('Failed to update task', 'error'); }
  };

  // Derived data
  const pendingTasks = tasks.filter(t => t.status !== 'done' && t.status !== 'completed');
  const completedTasks = tasks.filter(t => t.status === 'done' || t.status === 'completed');
  const expiringSoon = pantryItems.filter(item => {
    if (!item.expiry_date) return false;
    const days = differenceInDays(parseISO(item.expiry_date), new Date());
    return days >= 0 && days <= 3;
  });

  const PRIORITY_COLORS: Record<string, string> = { high: '#ef5350', medium: '#ffb74d', low: '#81c784' };
  const NOTE_COLORS = ['#ffb74d', '#81c784', '#64b5f6', '#f48fb1', '#ce93d8', '#80cbc4'];

  // ─── Desktop v2 (prototype-style shopping rail) ─────────────────────────
  if (isDesktop) {
    const activeList = lists[0] || null;
    return (
      <IonPage>
        <IonContent>
          <div className="fhv2-fpage">
            <FeaturePageHeader
              crumbs={[{ label: 'Home', path: '/app/home' }, { label: 'Shopping' }]}
              title={<>Shopping <em>lists.</em></>}
              lead="Shared lists that tick in real time — one source of truth across the household."
            />
            <div className="fhv2-fsection">
              <div className="fhv2-main-head">
                <div>
                  <h2><em>{activeList?.name || 'Your lists'}</em></h2>
                  <div className="fhv2-sub">{lists.length} shared list{lists.length === 1 ? '' : 's'} · {pantryItems.length} pantry items tracked</div>
                </div>
                <div className="fhv2-toolbar">
                  <input
                    ref={newListInputRef}
                    value={newListName}
                    onChange={e => setNewListName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleAddList()}
                    placeholder="New list name..."
                    style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--fhv2-line)', borderRadius: 10, padding: '10px 14px', color: '#fff', fontSize: 13, outline: 'none', height: 40, minWidth: 200 }}
                  />
                  <button className="fhv2-btn is-primary" onClick={handleAddList}>+ New list</button>
                </div>
              </div>

              <div className="fhv2-grid-rail">
                <div>
                  {lists.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--fhv2-muted)' }}>
                      <Icon name="shopping_cart" size={56} color="var(--fhv2-muted)" />
                      <p style={{ marginTop: 12, fontSize: 14 }}>No lists yet — create one above.</p>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {lists.map(list => (
                        <button
                          key={list.id}
                          onClick={() => history.push(`/app/lists/${list.id}`)}
                          className="fhv2-shop-item fhv2-unset"
                          style={{ background: 'rgba(255,255,255,0.03)' }}
                        >
                          <div className="fhv2-check" style={{ background: `${list.colour || '#c5512d'}33`, borderColor: list.colour || '#c5512d' }}>
                            <Icon name="shopping_cart" size={14} color={list.colour || '#c5512d'} />
                          </div>
                          <div>
                            <div className="fhv2-iname">{list.name}</div>
                            <div className="fhv2-imeta">{Number(list.pending_count) || 0} items · shared</div>
                          </div>
                          <span className="fhv2-qty">×{Number(list.pending_count) || 0}</span>
                          <Icon name="chevron_right" size={16} color="var(--fhv2-muted)" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="fhv2-stack">
                  <div className="fhv2-card">
                    <div className="fhv2-card-head"><h4>Running total</h4><span className="fhv2-more">Est.</span></div>
                    <div className="fhv2-big-num">£{((lists.length) * 5.36).toFixed(2)}</div>
                    <div style={{ fontSize: 12, color: 'var(--fhv2-muted)', marginTop: 4 }}>{lists.length} list{lists.length === 1 ? '' : 's'} · estimate</div>
                  </div>

                  {expiringSoon.length > 0 && (
                    <div className="fhv2-card">
                      <div className="fhv2-card-head"><h4>Suggested from pantry</h4></div>
                      {expiringSoon.slice(0, 3).map(item => (
                        <div className="fhv2-row" key={item.id}>
                          <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--fhv2-warn)' }} />
                          <div style={{ flex: 1 }}>
                            <div className="title">{item.name}</div>
                            <div className="sub">Expires soon</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {pendingTasks.length > 0 && (
                    <div className="fhv2-card">
                      <div className="fhv2-card-head"><h4>Open tasks</h4><span className="fhv2-more">{pendingTasks.length} left</span></div>
                      {pendingTasks.slice(0, 4).map(t => (
                        <div className="fhv2-row" key={t.id}>
                          <div style={{ flex: 1 }}>
                            <div className="title">{t.title}</div>
                            <div className="sub">{t.assigned_to_name || 'Unassigned'}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent />
        </IonRefresher>

        <FeatureMobileShell
          title="Lists"
          rightIcon="add"
          onRight={() => setShowAdd(true)}
          lead={<>{lists.length} lists, <em>{lists.reduce((s, l) => s + (Number(l.pending_count) || 0), 0)} items.</em><span className="sub">Shopping, tasks, pantry &amp; notes in one place.</span></>}
        >
          {/* Tab bar — v2 pill style. Negative horizontal margin lets the
              scroll area extend to the viewport edge (undoing the parent's
              16px padding) so tabs don't clip on narrow screens, with a
              matching padding so items stay visually aligned with the rest
              of the page when un-scrolled. */}
          <div style={{
            display: 'flex',
            gap: 6,
            overflowX: 'auto',
            scrollbarWidth: 'none',
            WebkitOverflowScrolling: 'touch',
            marginBottom: 16,
            marginLeft: -16,
            marginRight: -16,
            padding: '0 16px',
          }}>
            {TABS.map(t => {
              const on = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => { setTab(t.key); setShowAdd(false); }}
                  className="fhv2-unset"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px',
                    borderRadius: 999, flexShrink: 0, whiteSpace: 'nowrap',
                    border: on ? '1px solid rgba(197,81,45,0.3)' : '1px solid var(--fhv2-line)',
                    background: on ? 'rgba(197,81,45,0.12)' : 'transparent',
                    color: on ? '#fff' : 'var(--fhv2-muted)',
                    fontSize: 12, fontWeight: 600,
                  }}
                >
                  <Icon name={t.icon} size={14} color={on ? 'var(--fhv2-terra)' : 'currentColor'} />
                  {t.label}
                </button>
              );
            })}
          </div>

          {/* ──────── SHOPPING TAB ──────── */}
          {tab === 'shopping' && (
            <>
              <div className="fh-composer is-flat is-compact" style={{ marginBottom: 16 }}>
                <div className="fh-composer__pill">
                  <input
                    ref={newListInputRef}
                    className="fh-composer__input"
                    value={newListName}
                    onChange={e => setNewListName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleAddList()}
                    placeholder="New list name…"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleAddList}
                  disabled={!newListName.trim()}
                  aria-label="Create list"
                  className="fh-composer__action fh-composer__send"
                >
                  <Icon name="add" size={20} color="currentColor" />
                </button>
              </div>

              {lists.length === 0 ? (
                <div className="fhv2-empty">
                  <Icon name="shopping_cart" size={40} color="var(--fhv2-muted)" />
                  <h4>No lists yet</h4>
                  <p>Create one above.</p>
                </div>
              ) : lists.map((list: ShoppingList, i) => {
                const primary = i === 0;
                const count = list.pending_count || 0;
                return (
                  <div
                    key={list.id}
                    className={`fhv2-mcard ${primary ? 'is-primary' : ''}`}
                    onClick={() => history.push(`/app/lists/${list.id}`)}
                  >
                    <div className="fhv2-mcard-ic" style={{ background: `${list.colour || '#c5512d'}22`, color: list.colour || '#c5512d' }}>
                      <Icon name="shopping_cart" size={16} color="currentColor" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div className="fhv2-mcard-title">{list.name}</div>
                      <div className="fhv2-mcard-sub">{count} items remaining</div>
                    </div>
                    <div style={{ background: primary ? 'var(--fhv2-terra)' : 'rgba(255,255,255,0.06)', color: '#fff', fontWeight: 700, fontSize: 13, minWidth: 32, height: 32, padding: '0 10px', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {count}
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {/* ──────── TASKS TAB ──────── */}
          {tab === 'tasks' && (
            <div style={{ padding: '0 16px' }}>
              {/* Quick add */}
              <div className="fh-composer is-flat is-compact" style={{ marginBottom: 16 }}>
                <div className="fh-composer__pill">
                  <input
                    className="fh-composer__input"
                    value={newTaskTitle}
                    onChange={e => setNewTaskTitle(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleAddTask()}
                    placeholder="Add a task…"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleAddTask}
                  disabled={!newTaskTitle.trim()}
                  aria-label="Add task"
                  className="fh-composer__action fh-composer__send"
                >
                  <Icon name="add" size={20} color="currentColor" />
                </button>
              </div>

              {/* Progress */}
              {tasks.length > 0 && (
                <div style={{ background: 'var(--fh-overlay-subtle)', borderRadius: 12, padding: '12px 14px', marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 600, color: 'var(--fh-dark-text-secondary)', marginBottom: 6 }}>
                    <span>{completedTasks.length} of {tasks.length} done</span>
                    <span>{pendingTasks.length} remaining</span>
                  </div>
                  <div style={{ height: 4, borderRadius: 2, background: 'var(--fh-overlay-medium)' }}>
                    <div style={{ height: '100%', borderRadius: 2, background: '#81c784', width: `${(completedTasks.length / tasks.length) * 100}%`, transition: 'width 0.3s' }} />
                  </div>
                </div>
              )}

              {/* Task list */}
              {pendingTasks.length === 0 && completedTasks.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <Icon name="check_circle" size={48} color="var(--fh-dark-text-muted)" />
                  <p style={{ color: 'var(--fh-dark-text-muted)', fontSize: 14, marginTop: 12 }}>All clear! No tasks.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {pendingTasks.map((task: Task) => (
                    <div key={task.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', background: 'var(--fh-overlay-subtle)', borderRadius: 12 }}>
                      <button
                        onClick={() => handleToggleTask(task)}
                        style={{ width: 24, height: 24, borderRadius: '50%', border: `2px solid ${PRIORITY_COLORS[task.priority] || '#81c784'}`, background: 'transparent', cursor: 'pointer', flexShrink: 0 }}
                        aria-label={`Complete ${task.title}`}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p title={task.title} style={{ fontSize: 14, fontWeight: 600, color: 'var(--fh-dark-text)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{task.title}</p>
                        {task.due_date && (
                          <p style={{ fontSize: 11, color: 'var(--fh-dark-text-muted)', margin: '2px 0 0' }}>
                            Due {format(parseISO(task.due_date), 'd MMM')}
                            {task.assigned_to_name && ` \u00B7 ${task.assigned_to_name}`}
                          </p>
                        )}
                      </div>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: PRIORITY_COLORS[task.priority] || '#81c784', flexShrink: 0 }} />
                    </div>
                  ))}
                  {completedTasks.length > 0 && (
                    <>
                      <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--fh-dark-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '8px 0 4px' }}>
                        Completed ({completedTasks.length})
                      </p>
                      {completedTasks.slice(0, 5).map((task: Task) => (
                        <div key={task.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--fh-overlay-subtle)', borderRadius: 12, opacity: 0.5 }}>
                          <button
                            onClick={() => handleToggleTask(task)}
                            style={{ width: 24, height: 24, borderRadius: '50%', border: '2px solid #81c784', background: 'rgba(129,199,132,0.2)', cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            aria-label={`Uncomplete ${task.title}`}
                          >
                            <Icon name="check" size={14} color="#81c784" />
                          </button>
                          <p style={{ fontSize: 14, color: 'var(--fh-dark-text-muted)', margin: 0, textDecoration: 'line-through' }}>{task.title}</p>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ──────── NOTES TAB ──────── */}
          {tab === 'notes' && (
            <div style={{ padding: '0 16px' }}>
              {/* Quick add */}
              <div className="fh-composer is-flat is-compact" style={{ marginBottom: 16 }}>
                <div className="fh-composer__pill">
                  <input
                    className="fh-composer__input"
                    value={newNoteTitle}
                    onChange={e => setNewNoteTitle(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleAddNote()}
                    placeholder="New note title…"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleAddNote}
                  disabled={!newNoteTitle.trim()}
                  aria-label="Add note"
                  className="fh-composer__action fh-composer__send"
                >
                  <Icon name="add" size={20} color="currentColor" />
                </button>
              </div>

              {notes.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <Icon name="sticky_note_2" size={48} color="var(--fh-dark-text-muted)" />
                  <p style={{ color: 'var(--fh-dark-text-muted)', fontSize: 14, marginTop: 12 }}>No notes yet.</p>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
                  {notes.map((note: SharedNote) => (
                    <button
                      key={note.id}
                      onClick={() => history.push('/app/notes')}
                      style={{
                        padding: '14px', background: 'var(--fh-overlay-subtle)', borderRadius: 14,
                        border: 'none', cursor: 'pointer', textAlign: 'left',
                        borderTop: `3px solid ${note.colour || '#ffb74d'}`,
                      }}
                    >
                      <p title={note.title} style={{ fontSize: 14, fontWeight: 700, color: 'var(--fh-dark-text)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {note.pinned && '📌 '}{note.title}
                      </p>
                      {note.content && (
                        <p style={{ fontSize: 12, color: 'var(--fh-dark-text-muted)', margin: '4px 0 0', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' as const, overflow: 'hidden' }}>
                          {note.content}
                        </p>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </FeatureMobileShell>
      </IonContent>
    </IonPage>
  );
};

export default ListsHubPage;
